/* TEXT AREA DOCUMENT CONVERSION — the app's paragraphs/runs model <-> the
   ProseMirror/Tiptap JSON the editor works in.

   Pure: no DOM, no Tiptap, no S. Both directions are plain data transforms,
   which is the whole point — the editor's document is a real, schema-checked
   structure rather than whatever HTML a contenteditable happened to leave
   behind, so converting is a mapping instead of the reverse-engineering the
   previous editor had to do (and kept getting wrong: styles silently lost on
   a Backspace block-merge, sizes concatenating into nonsense, the toolbar
   unable to say what was under the cursor).

   The app's model is the persisted one — it's what's inside every saved
   project and preset, and what scene/textarea.js rasterises — so it stays
   exactly as it was. Tiptap's JSON is the transient side.

     model:  [{ align, list, lineHeight?, runs:[{text,font,weight,size,color,italic,underline}] }]
     tiptap: { type:'doc', content:[ paragraph | bulletList | orderedList ] }

   Only what the model can represent is allowed in the schema (see the editor's
   StarterKit config) — no headings, no code, no links. So there is no such
   thing as content the editor can hold but this cannot store, including on
   paste, which is exactly the class of bug the old DOM-scraping version
   couldn't rule out. */

/** The style a run has when nothing has been applied to it. */
export const DEFAULT_RUN = {
  font: 'Inter', weight: 400, size: 100, color: '#ffffff', italic: false, underline: false
};

/* ── model -> tiptap ─────────────────────────────────────────────────────── */

/* Weight is carried two different ways on purpose. 700 becomes a real `bold`
   mark so Tiptap's own Bold button and Cmd+B light up and toggle it the way
   every editor does; any OTHER non-default weight (100-600, 800, 900) has no
   equivalent mark and rides as an explicit fontWeight attribute instead.
   Reading back (below) lets an explicit weight win over the bold mark, which
   is the same precedence the previous editor's own parser used. */
function runToTextNode(run) {
  if (!run || !run.text) return null;          // ProseMirror has no empty text node
  const marks = [];
  const weight = +run.weight || 400;
  if (weight === 700) marks.push({ type: 'bold' });
  if (run.italic) marks.push({ type: 'italic' });
  if (run.underline) marks.push({ type: 'underline' });

  const style = {};
  if (run.font) style.fontFamily = run.font;
  if (run.color) style.color = run.color;
  if (run.size != null && +run.size !== 100) style.fontSize = (+run.size) + '%';
  if (weight !== 400 && weight !== 700) style.fontWeight = String(weight);
  if (Object.keys(style).length) marks.push({ type: 'textStyle', attrs: style });

  return { type: 'text', text: run.text, marks };
}

function paragraphNode(para) {
  const content = (para.runs || []).map(runToTextNode).filter(Boolean);
  const attrs = { textAlign: para.align || 'left' };
  if (para.lineHeight != null) attrs.lineHeight = String(para.lineHeight);
  const node = { type: 'paragraph', attrs };
  // An empty paragraph carries no `content` key at all — an empty array is
  // not the same thing to ProseMirror and fails schema validation.
  if (content.length) node.content = content;
  return node;
}

/** @returns {object} a Tiptap/ProseMirror `doc` node for the editor's `content`. */
export function paragraphsToDoc(paragraphs) {
  const paras = (paragraphs && paragraphs.length) ? paragraphs : [{ align: 'left', list: null, runs: [] }];
  const content = [];
  let i = 0;
  while (i < paras.length) {
    const list = paras[i].list;
    if (!list) { content.push(paragraphNode(paras[i])); i++; continue; }
    // Consecutive same-kind list paragraphs are ONE list node with an item
    // each — the model stores them flat, the editor needs them nested.
    const items = [];
    while (i < paras.length && paras[i].list === list) {
      items.push({ type: 'listItem', content: [paragraphNode(paras[i])] });
      i++;
    }
    content.push({ type: list === 'number' ? 'orderedList' : 'bulletList', content: items });
  }
  return { type: 'doc', content };
}

/* ── tiptap -> model ─────────────────────────────────────────────────────── */

/* Pasted HTML is the reason these three normalisers exist: content built by
   the editor itself is already in the right shape, but a paste runs through
   the browser's own HTML parsing first, which canonicalises colours to
   rgb(), brings whole font stacks along, and can carry sizes in units this
   model has no way to express. */
function rgbToHex(css) {
  const m = /rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(css || '');
  if (!m) return null;
  const h = n => Math.max(0, Math.min(255, Math.round(+n))).toString(16).padStart(2, '0');
  return '#' + h(m[1]) + h(m[2]) + h(m[3]);
}
function normColor(v) {
  if (!v) return null;
  const s = String(v).trim();
  if (s[0] === '#') return s.length === 4                       // #abc -> #aabbcc
    ? '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3]
    : s.slice(0, 7);
  return rgbToHex(s);
}
function normFamily(v) {
  if (!v) return null;
  // First family of a stack, unquoted: `"Chivo", sans-serif` -> `Chivo`
  return String(v).split(',')[0].trim().replace(/^["']|["']$/g, '') || null;
}
/* Sizes are a PERCENTAGE of the block's own Base size (see DEF.textarea), not
   an absolute unit, so a pasted `font-size:14px` has no meaningful conversion —
   there is no fixed relationship between a source document's pixels and this
   block's metres. Anything that isn't a percentage falls back to the default
   rather than inventing a number. */
function normSize(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s.endsWith('%')) return null;
  const n = parseFloat(s);
  return isFinite(n) && n > 0 ? n : null;
}

function textNodeToRun(node) {
  const marks = node.marks || [];
  const style = (marks.find(m => m.type === 'textStyle') || {}).attrs || {};
  const bold = marks.some(m => m.type === 'bold');
  const explicitWeight = parseInt(style.fontWeight, 10);

  return {
    text: node.text || '',
    font: normFamily(style.fontFamily) || DEFAULT_RUN.font,
    // An explicit weight wins over the bold mark — same precedence the old
    // parser applied, so a menu-picked 900 isn't flattened to 700.
    weight: isFinite(explicitWeight) && explicitWeight ? explicitWeight : (bold ? 700 : 400),
    size: normSize(style.fontSize) ?? DEFAULT_RUN.size,
    color: normColor(style.color) || DEFAULT_RUN.color,
    italic: marks.some(m => m.type === 'italic'),
    underline: marks.some(m => m.type === 'underline')
  };
}

function paragraphToModel(node, list) {
  const attrs = node.attrs || {};
  const runs = (node.content || [])
    .filter(c => c.type === 'text')
    .map(textNodeToRun)
    .filter(r => r.text.length);
  const para = {
    align: attrs.textAlign || 'left',
    list: list || null,
    runs: runs.length ? runs : [{ ...DEFAULT_RUN, text: '' }]
  };
  const lh = parseFloat(attrs.lineHeight);
  if (isFinite(lh) && lh > 0) para.lineHeight = lh;
  return para;
}

/** @returns {object[]} the app's paragraphs model — always at least one paragraph. */
export function docToParagraphs(doc) {
  const out = [];
  for (const block of (doc && doc.content) || []) {
    if (block.type === 'bulletList' || block.type === 'orderedList') {
      const kind = block.type === 'orderedList' ? 'number' : 'bullet';
      for (const item of block.content || []) {
        // A list item holds block content; only its paragraphs are
        // representable here, and the schema allows nothing else inside one.
        for (const child of item.content || []) {
          if (child.type === 'paragraph') out.push(paragraphToModel(child, kind));
        }
      }
    } else if (block.type === 'paragraph') {
      out.push(paragraphToModel(block, null));
    }
  }
  return out.length ? out : [{ align: 'left', list: null, runs: [{ ...DEFAULT_RUN, text: '' }] }];
}
